import { isReplacedMilestone, isMilestoneSettledForClosure } from '../utils/milestoneHistory';
import { contractTitle } from '../utils/contractTitle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Contract, Milestone, ProjectFundingContract, ProjectFundingMilestone } from '../types';
import { currentContractWorkflowMilestone } from '../utils/milestoneWorkflowStatus';
import MilestoneFlowTimeline from './MilestoneFlowTimeline';
import { effectiveTimelineDeliveryStatus, timelineDeliveryPresentation } from '../utils/timelineDeliveryPresentation';
import { pendingTimelineReplacement, timelineFundingNeedOre } from '../utils/timelineFunding';
import { replacementTimelineProjection } from '../utils/replacementTimelineProjection';
import { combinedAgreementTimeline, TimelineAgreementContext } from '../utils/combinedAgreementTimeline';
import { isRevisedDraft } from '../utils/revisedDraft';
import { agreementHistoryTree } from '../utils/agreementHistoryTree';
import { agreementLabel, agreementRoot, agreementVariationNumber } from '../utils/agreementIdentity';

export interface ContractTotal {
  key: string;
  icon: string;
  label: string;
  value: string;
  /** Secondary line under the value, e.g. "Efter ROT 1 700 kr". */
  detail?: string;
  /** Share (0–100) drawn as a bar under the value inside the frame. */
  progress?: number;
  tone?: 'success' | 'warning';
  action?: { label: string; onClick: () => void };
  /** Parts of the value (e.g. each parallel stage), listed on demand in the frame. */
  breakdown?: { key: string; label: string; value: string }[];
}

/** Per-contract content that the page owns (actions, totals, notices).
 * Inside a contract frame it is laid out in the frame; otherwise it follows
 * the timeline in the same order as before. */
/** The header status split up, so a compact header can place its parts apart. */
interface FrameStatusParts {
  pill: React.ReactNode;
  event: React.ReactNode;
  attrs: Record<string, string | undefined>;
}

export interface ContractFrameSlots {
  /** Replaces the full notice in the frame header, e.g. "ÄTA #3 gäller". */
  headerBadge?: React.ReactNode;
  notice?: React.ReactNode;
  actions?: React.ReactNode;
  details?: React.ReactNode;
  totals?: ContractTotal[];
  totalsNote?: React.ReactNode;
  /** The timeline directly under the header, above the key figures (start page). */
  timelineFirst?: boolean;
  /** Shown right under the timeline when it comes first, inside the folding body. */
  afterTimeline?: React.ReactNode;
  /** Start page: contract and name on the first line, what happens (and any
   * ÄTA) in grey below, and one status badge plus the toggle on the right. */
  compactHeader?: boolean;
}

interface ContractTimelineProps {
  contract: Contract;
  milestones: Milestone[];
  relatedContracts?: Contract[];
  relatedMilestones?: Milestone[];
  parentContract?: Contract | null;
  projectFunded?: boolean;
  projectCompleted?: boolean;
  projectExternallyDisputed?: boolean;
  onManageStarts?: (milestone: Milestone) => void;
  canManageStarts?: boolean;
  viewerRole?: 'company' | 'person';
  fundingMilestones?: ProjectFundingMilestone[];
  fundingContract?: ProjectFundingContract;
  relatedFundingContracts?: ProjectFundingContract[];
  onPayContract?: (contract?: Contract) => void;
  onSignContract?: (contract: Contract) => void;
  contractSigningDisabled?: boolean;
  renderMilestoneActions?: (
    milestone: Milestone,
    closeDetails: () => void
  ) => React.ReactNode;
  agreementProjection?: boolean;
  agreementReadOnly?: boolean;
  combinedRoot?: Contract;
  milestoneContexts?: Record<string, TimelineAgreementContext>;
  /** Lays the timeline out inside a contract frame, with its status line
   * handed over for the frame's header. */
  renderFrame?: (parts: { status: React.ReactNode; statusParts?: FrameStatusParts; timeline: React.ReactNode; closed: boolean }) => React.ReactElement;
  frameSlots?: ContractFrameSlots;
  /** Stages the viewer must act on (attest); their nodes pulse red. */
  actionMilestoneIds?: string[];
  /** The customer's name, for "sent back by …" (company view). */
  customerName?: string;
  /** Company side: opens a contract the customer sent back in its editor. */
  onReviseContract?: (contract: Contract) => void;
}

type StepState = 'done' | 'current' | 'pending' | 'warning' | 'error' | 'closed';

interface DraftStep {
  type: 'single';
  key: string;
  label: string;
  state: StepState;
}

type Step = DraftStep;

interface PhaseStep extends DraftStep {
  children?: Step[];
}

function phaseState(children: Step[]): StepState {
  if (children.some(step => step.state === 'error')) return 'error';
  if (children.some(step => step.state === 'warning')) return 'warning';
  if (children.some(step => step.state === 'current')) return 'current';
  if (children.every(step => step.state === 'done')) return 'done';
  return 'pending';
}

export default function ContractTimeline(props: ContractTimelineProps): React.ReactElement {
  const { t } = useTranslation();
  const combined = !props.agreementReadOnly && props.contract.versionStatus !== 'superseded' && props.relatedContracts && props.relatedMilestones
    ? combinedAgreementTimeline(props.contract, props.relatedContracts, props.relatedMilestones) : null;
  if (combined) {
    const funding = props.relatedFundingContracts?.find(item => item.contractId === combined.contract.id);
    const amendmentNumbers = combined.history.filter(group => group.contract.stageVariation)
      .map(group => agreementVariationNumber(group.contract)!).sort((a, b) => a - b);
    // The chain is in signing order: the last agreement is the one in force,
    // unless it still awaits activation — then the one before it still applies.
    const chain = combined.history.map(group => group.contract);
    const inForce = combined.pending ? chain.length - 2 : chain.length - 1;
    const rowInfo = (agreement: Contract): Pick<AgreementHistoryRowProps, 'status' | 'supersededBy' | 'stageChanges'> => {
      const index = chain.findIndex(item => item.id === agreement.id);
      return {
        status: index === inForce ? 'current' : index > inForce ? 'pending' : 'superseded',
        supersededBy: index < inForce ? agreementVariationNumber(chain[inForce]) ?? undefined : undefined,
        stageChanges: agreement.stageVariation ? amendmentStageChanges(agreement, props.relatedMilestones ?? []) : undefined
      };
    };
    const history = (
      <details className="agreement-history" data-testid="combined-contract-history">
        <summary className="agreement-history-summary">{t('combinedTimeline.history')}</summary>
        <p className="small text-muted">{t('combinedTimeline.historyHint')}</p>
        <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
          {agreementHistoryTree(combined.history).map(node => <li key={node.group.contract.id}>
            <AgreementHistoryRow {...node.group} {...rowInfo(node.group.contract)} label={agreementLabel(node.group.contract, props.relatedContracts ?? [], t)} viewerRole={props.viewerRole} />
            {node.amendments.length > 0 && <ul className="agreement-history-amendments list-unstyled d-flex flex-column gap-2 mb-0 mt-2">
              {node.amendments.map(group => <li key={group.contract.id} className="agreement-history-amendment">
                <AgreementHistoryRow {...group} {...rowInfo(group.contract)} label={agreementLabel(group.contract, props.relatedContracts ?? [], t)} viewerRole={props.viewerRole} />
              </li>)}
            </ul>}
          </li>)}
        </ul>
      </details>
    );
    return <SingleContractTimeline {...props} agreementProjection combinedRoot={combined.root}
      contract={combined.contract} milestones={combined.milestones} milestoneContexts={combined.contexts}
      projectFunded={timelineFundingNeedOre(combined.contract, funding) === 0} fundingContract={funding}
      onPayContract={() => props.onPayContract?.(combined.contract)}
      renderFrame={({ status, statusParts, timeline, closed }) => <ContractFrame root={combined.root} amendmentNumbers={amendmentNumbers}
        slots={props.frameSlots} status={status} statusParts={statusParts} timeline={timeline} history={history} closed={closed} />} />;
  }
  const slots = props.frameSlots;
  const trailing = slots && <>
    {slots.notice}
    {slots.actions}
    {slots.details}
    {(slots.totals?.length || slots.totalsNote) && <div className="d-flex flex-wrap gap-3 text-muted mb-3 px-1 timeline-totals">
      {slots.totals?.map(total => <span key={total.key}>
        <i className={`feather-${total.icon} me-1`}></i>{total.label}: {total.value}{total.detail && ` (${total.detail})`}
        {total.action && <button type="button" className="btn btn-sm btn-warning ms-2" onClick={total.action.onClick}>{total.action.label}</button>}
      </span>)}
      {slots.totalsNote && <span><i className="feather-lock me-1"></i>{slots.totalsNote}</span>}
    </div>}
  </>;
  if (props.contract.versionStatus === 'superseded') return <>
    <details className="border rounded p-3 mb-3" data-testid="historical-contract-milestones">
      <summary className="fw-semibold py-3">{agreementLabel(props.contract, props.relatedContracts ?? [], t)}</summary>
      <MilestoneFlowTimeline contract={props.contract} milestones={props.milestones} contractSteps={[]} historyOnly viewerRole={props.viewerRole} />
    </details>
    {trailing}
  </>;
  if (slots) return <SingleContractTimeline {...props} renderFrame={({ status, statusParts, timeline, closed }) =>
    <ContractFrame root={props.contract} amendmentNumbers={[]} slots={slots} status={status} statusParts={statusParts} timeline={timeline} closed={closed} />} />;
  return <SingleContractTimeline {...props} />;
}

/** The frame around one contract: header with number, name and status,
 * the contract's key figures, its next action and then the timeline (and,
 * for a chain with ÄTA, the agreement history). Every frame can be folded
 * to its header; a closed contract is folded. Until the viewer toggles it,
 * the fold follows the contract's current state — data can arrive in steps
 * (stages after the contract), and a contract that only looked closed while
 * loading must still open once its stages are known. The content stays
 * mounted while folded so open dialogs and tests keep their elements. */
function ContractFrame({ root, amendmentNumbers, slots, status, statusParts, timeline, history, closed = false }: {
  root: Contract;
  amendmentNumbers: number[];
  slots?: ContractFrameSlots;
  status: React.ReactNode;
  statusParts?: FrameStatusParts;
  timeline: React.ReactNode;
  history?: React.ReactNode;
  closed?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const [choice, setChoice] = React.useState<boolean | null>(null);
  const [openBreakdowns, setOpenBreakdowns] = React.useState<Set<string>>(new Set());
  const toggleBreakdown = (key: string) => setOpenBreakdowns(previous => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const open = choice ?? !closed;
  const bodyId = `contract-group-body-${root.id}`;
  const rootTitle = contractTitle(root);
  return <section className={`contract-group${open ? '' : ' is-collapsed'}`} data-testid="contract-group" data-contract-group={root.id}
    data-contract-closed={closed || undefined}>
    {slots?.compactHeader ? (() => {
      // ÄTA as grey text; a caller's badge only when the chain names none.
      const amendments = amendmentNumbers.length > 0 ? <span className="contract-group-meta">
        {t('combinedTimeline.includesAmendments', { numbers: amendmentNumbers.map(number => `#${number}`).join(', ') })}
      </span> : slots.headerBadge;
      const event = statusParts?.event;
      return <header className="contract-group-header is-compact">
        <div className="contract-group-heading">
          <div className="contract-group-heading-top">
            <span className="badge rounded-pill agreement-history-badge-contract">{t('combinedTimeline.agreementLabel')}</span>
            <h3 className="contract-group-title">
              {rootTitle
                ? t('combinedTimeline.entryTitle', { number: root.contractNumber, title: rootTitle })
                : t('combinedTimeline.entryNumber', { number: root.contractNumber })}
            </h3>
          </div>
          {(event || amendments) && <div className="contract-group-heading-sub">
            {event}{event && amendments && <span className="contract-group-meta contract-group-heading-dot" aria-hidden="true">·</span>}{amendments}
          </div>}
        </div>
        <div className="contract-group-side" {...(closed ? {} : statusParts?.attrs)} role="status">
          {/* One status: a closed contract says so, instead of its last event's badge. */}
          {closed ? <span className="contract-group-status-pill is-success">{t('combinedTimeline.closed')}</span> : statusParts?.pill}
        </div>
        <button type="button" className="contract-group-toggle" aria-expanded={open} aria-controls={bodyId}
          onClick={() => setChoice(!open)}>
          {t(open ? 'combinedTimeline.collapse' : 'combinedTimeline.expand')}
          <i className={`bi bi-chevron-${open ? 'up' : 'down'} ms-2`} aria-hidden="true" />
        </button>
      </header>;
    })() : (
      <header className="contract-group-header">
        <span className="badge rounded-pill agreement-history-badge-contract">{t('combinedTimeline.agreementLabel')}</span>
        <h3 className="contract-group-title">
          {rootTitle
            ? t('combinedTimeline.entryTitle', { number: root.contractNumber, title: rootTitle })
            : t('combinedTimeline.entryNumber', { number: root.contractNumber })}
        </h3>
        {slots?.headerBadge ?? (amendmentNumbers.length > 0 && <span className="contract-group-meta">
          {t('combinedTimeline.includesAmendments', { numbers: amendmentNumbers.map(number => `#${number}`).join(', ') })}
        </span>)}
        {closed && <span className="contract-group-status-pill is-success">{t('combinedTimeline.closed')}</span>}
        {status}
        <button type="button" className="contract-group-toggle" aria-expanded={open} aria-controls={bodyId}
          onClick={() => setChoice(!open)}>
          {t(open ? 'combinedTimeline.collapse' : 'combinedTimeline.expand')}
          <i className={`bi bi-chevron-${open ? 'up' : 'down'} ms-2`} aria-hidden="true" />
        </button>
      </header>
    )}
    <div id={bodyId} hidden={!open}>
      {slots?.timelineFirst && <div className="contract-group-body">{timeline}{slots.afterTimeline}</div>}
      {(slots?.totals?.length || slots?.totalsNote) && <dl className="contract-group-totals" data-testid="contract-group-totals">
        {slots.totals?.map(total => <div key={total.key} className={`contract-group-total${total.tone ? ` is-${total.tone}` : ''}`} data-contract-total={total.key}>
          <dt>{total.label}</dt>
          <dd>{total.value}</dd>
          {total.detail && <dd className="contract-group-total-detail">{total.detail}</dd>}
          {total.breakdown && <dd className="contract-group-total-breakdown">
            <button type="button" className="contract-group-total-toggle" aria-expanded={openBreakdowns.has(total.key)}
              aria-controls={`contract-total-${root.id}-${total.key}`} onClick={() => toggleBreakdown(total.key)}>
              {t(openBreakdowns.has(total.key) ? 'combinedTimeline.collapse' : 'combinedTimeline.expand')}
              <i className={`bi bi-chevron-${openBreakdowns.has(total.key) ? 'up' : 'down'} ms-1`} aria-hidden="true" />
            </button>
            <ul id={`contract-total-${root.id}-${total.key}`} className="contract-group-total-list list-unstyled" hidden={!openBreakdowns.has(total.key)}>
              {total.breakdown.map(part => <li key={part.key}><span>{part.label}</span><span>{part.value}</span></li>)}
            </ul>
          </dd>}
          {total.progress !== undefined && <dd className="contract-group-total-progress progress" role="progressbar"
            aria-label={total.label} aria-valuenow={Math.round(total.progress)} aria-valuemin={0} aria-valuemax={100}>
            <div className="progress-bar" style={{ width: `${total.progress}%` }} />
          </dd>}
          {total.action && <dd className="contract-group-total-action">
            <button type="button" className="btn btn-sm btn-warning" onClick={total.action.onClick}>{total.action.label}</button>
          </dd>}
        </div>)}
        {slots.totalsNote && <div className="contract-group-total contract-group-total-note">
          <i className="feather-lock me-1" aria-hidden="true"></i>{slots.totalsNote}
        </div>}
      </dl>}
      {(() => {
        const rest = [
          !slots?.headerBadge && slots?.notice,
          slots?.actions && <div key="todo" className="contract-group-todo" data-testid="contract-group-todo">
            <div className="contract-group-todo-label">
              <i className="feather-flag me-2" aria-hidden="true"></i>{t('combinedTimeline.todo')}
            </div>
            {slots.actions}
          </div>,
          slots?.details,
          !slots?.timelineFirst && timeline,
          history
        ].filter(Boolean);
        // No empty padded box under the key figures when the timeline moved up.
        return rest.length > 0 && <div className="contract-group-body">
          {rest.map((part, index) => <React.Fragment key={index}>{part}</React.Fragment>)}
        </div>;
      })()}
    </div>
  </section>;
}

type AgreementHistoryStatus = 'current' | 'superseded' | 'pending';

interface StageChanges {
  changed: number[];
  removed: number[];
  added: number;
}

/** What an ÄTA did to the stages it amends, from its stored links: selected
 * stages with a successor were changed, those without one were removed, and
 * own stages that replace nothing were added. */
function amendmentStageChanges(amendment: Contract, stages: Milestone[]): StageChanges {
  const variation = amendment.stageVariation!;
  const own = stages.filter(stage => stage.contractId === amendment.id);
  const restored = new Set((variation.restoredMilestones ?? []).map(link => link.milestoneId));
  const successors = new Set(own.flatMap(stage => stage.replacesMilestoneId ? [stage.replacesMilestoneId] : []));
  const numberOf = (id: string) => stages.find(stage => stage.id === id)?.milestoneNumber;
  const numbers = (ids: string[]) => ids.flatMap(id => numberOf(id) ?? []).sort((a, b) => a - b);
  return {
    changed: numbers(variation.selectedMilestoneIds.filter(id => successors.has(id))),
    removed: numbers(variation.selectedMilestoneIds.filter(id => !successors.has(id))),
    added: own.filter(stage => !stage.replacesMilestoneId && !restored.has(stage.id)).length
  };
}

interface AgreementHistoryRowProps {
  label: string;
  contract: Contract;
  milestones: Milestone[];
  viewerRole?: 'company' | 'person';
  status: AgreementHistoryStatus;
  supersededBy?: number;
  stageChanges?: StageChanges;
}

/** One agreement in the history list: a compact toggle row whose stages stay
 * mounted but hidden while collapsed. An ÄTA summarises its price change and
 * stage changes; every row says whether the agreement is still in force. */
function AgreementHistoryRow({ contract, milestones, viewerRole, status, supersededBy, stageChanges, label }: AgreementHistoryRowProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const amendment = !!contract.stageVariation;
  const locale = i18n.language === 'en' ? 'en-GB' : 'sv-SE';
  const panelId = `agreement-history-${contract.id}`;
  const title = contractTitle(contract);
  const signedAt = contract.signingCompletedAt
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(contract.signingCompletedAt))
    : null;
  const price = (ore: number) => new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'SEK', signDisplay: 'exceptZero', minimumFractionDigits: 0, maximumFractionDigits: 2
  }).format(ore / 100);
  const listed = (numbers: number[], key: string, countKey: string) => !numbers.length ? [] : [numbers.length > 3
    ? t(countKey, { count: numbers.length })
    : t(key, { numbers: numbers.join(', ') })];
  const gross = contract.stageVariation?.grossDeltaOre ?? 0;
  const afterRot = contract.stageVariation?.afterRotDeltaOre ?? gross;
  const changes = stageChanges ? [
    ...listed(stageChanges.changed, 'combinedTimeline.stageChanged', 'combinedTimeline.stageChangedCount'),
    ...listed(stageChanges.removed, 'combinedTimeline.stageRemoved', 'combinedTimeline.stageRemovedCount'),
    ...(stageChanges.added ? [t('combinedTimeline.stageAdded', { count: stageChanges.added })] : [])
  ] : [];
  const subline = amendment ? <>
    <strong>{gross ? price(gross) : t('combinedTimeline.noPriceChange')}</strong>
    {afterRot !== gross && <> ({t('combinedTimeline.priceAfterRot', { amount: price(afterRot) })})</>}
    {changes.map(part => <React.Fragment key={part}> · {part}</React.Fragment>)}
  </> : supersededBy ? t('combinedTimeline.amendedBy', { number: supersededBy }) : null;
  const tone = status === 'current' ? 'success' : status === 'pending' ? 'warning' : 'secondary';
  // Every signed ÄTA applies together with the contract, so none is singled
  // out as the one in force (ClickUp 869fgj5tr).
  const statusLabel = t(status === 'pending' ? 'combinedTimeline.statusPending'
    : amendment ? 'combinedTimeline.statusIncluded'
      : status === 'current' ? 'combinedTimeline.statusCurrent' : 'combinedTimeline.statusAmended');
  // Rendered twice: beside the type badge up to lg, after the text from lg.
  const statusBadge = (className: string) => <span className={`contract-group-status-pill agreement-history-status is-${tone} ${className}`}>
    {statusLabel}
  </span>;
  return <div className={`agreement-history-row${open ? ' is-open' : ''}`} data-agreement-history={contract.id} data-agreement-status={status}>
    <button type="button" className="agreement-history-toggle" aria-expanded={open} aria-controls={panelId}
      onClick={() => setOpen(value => !value)}>
      <i className={`bi bi-chevron-${open ? 'down' : 'right'} agreement-history-chevron d-none d-lg-inline-block`} aria-hidden="true" />
      <span className="agreement-history-badges">
        <span className={`badge rounded-pill ${amendment ? 'agreement-history-badge-amendment' : 'agreement-history-badge-contract'}`}>
          {t(amendment ? 'combinedTimeline.amendmentLabel' : 'combinedTimeline.agreementLabel')}
        </span>
        {statusBadge('d-lg-none')}
      </span>
      <span className="agreement-history-text">
        <span className="agreement-history-title">
          {amendment ? <>{label}{title ? ` · ${title}` : ''}</> : title
            ? t('combinedTimeline.entryTitle', { number: contract.contractNumber, title })
            : t('combinedTimeline.entryNumber', { number: contract.contractNumber })}
        </span>
        {subline && <span className="agreement-history-subline">{subline}</span>}
      </span>
      {statusBadge('d-none d-lg-inline-flex')}
      {signedAt && <span className="agreement-history-meta d-none d-lg-inline">
        {t(amendment ? 'combinedTimeline.approvedOn' : 'combinedTimeline.signedOn', { date: signedAt })}
      </span>}
      <span className="agreement-history-action">
        {t(open ? 'combinedTimeline.hideStages' : 'combinedTimeline.showStages')}
      </span>
      <i className={`bi bi-chevron-${open ? 'up' : 'down'} agreement-history-chevron-end d-lg-none`} aria-hidden="true" />
    </button>
    <div id={panelId} className="agreement-history-panel" hidden={!open}>
      <MilestoneFlowTimeline contract={{ ...contract, versionStatus: 'superseded' }} milestones={milestones}
        contractSteps={[]} historyOnly viewerRole={viewerRole} />
      <a className="agreement-history-open-link" href={`/projects/${encodeURIComponent(contract.projectId)}?tab=contract&contractId=${encodeURIComponent(contract.id)}`}>
        {t('combinedTimeline.openAgreement')}
        <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
      </a>
    </div>
  </div>;
}

function SingleContractTimeline({
  contract: currentContract,
  milestones: currentMilestones,
  relatedContracts = [],
  relatedMilestones = [],
  parentContract,
  projectFunded,
  projectCompleted = false,
  projectExternallyDisputed = false,
  onManageStarts,
  canManageStarts = false,
  viewerRole,
  fundingMilestones: currentFundingMilestones,
  fundingContract: currentFundingContract,
  onPayContract,
  onSignContract,
  contractSigningDisabled,
  renderMilestoneActions,
  agreementProjection = false,
  agreementReadOnly = false,
  combinedRoot,
  milestoneContexts,
  renderFrame,
  actionMilestoneIds,
  customerName,
  onReviseContract
}: ContractTimelineProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'sv';
  const pendingReplacement = !agreementProjection && !projectCompleted && !projectExternallyDisputed
    ? pendingTimelineReplacement(currentContract, relatedContracts) : undefined;
  const projection = pendingReplacement ? replacementTimelineProjection(currentMilestones,
    relatedMilestones.filter(milestone => milestone.contractId === pendingReplacement.id)) : undefined;
  const contract = pendingReplacement ?? currentContract;
  const milestones = projection?.milestones ?? currentMilestones;
  const fundingContract = pendingReplacement ? undefined : currentFundingContract;
  // A stage can be drawn under another id than the one the page acts on: a
  // continued stage under its original, a projected replacement under its
  // own. Follow those links so the drawn node is the one that pulses.
  const actionNodeIds = (() => {
    if (!actionMilestoneIds?.length) return undefined;
    const ids = new Set(actionMilestoneIds);
    const known = [...currentMilestones, ...relatedMilestones];
    actionMilestoneIds.forEach(id => {
      const seen = new Set<string>();
      let stage = known.find(item => item.id === id);
      while (stage?.replacesMilestoneId && !seen.has(stage.id)) {
        seen.add(stage.id);
        ids.add(stage.replacesMilestoneId);
        const previousId: string = stage.replacesMilestoneId;
        stage = known.find(item => item.id === previousId);
      }
    });
    projection?.actionSources.forEach((source, targetId) => {
      if (ids.has(source.id)) ids.add(targetId);
    });
    return ids;
  })();
  const fundingMilestones = projection
    ? [...projection.actionSources].flatMap(([targetId, source]) => {
      const funding = currentFundingMilestones?.find(item => item.milestoneId === source.id);
      return funding ? [{ ...funding, milestoneId: targetId }] : [];
    }) : currentFundingMilestones;

  // Compute state per phase
  const draftStatus = contract.draftStatus || 'none';
  const isSignedByBoth = draftStatus === 'signed_by_both' || contract.signingStatus === 'signed' ||
    (contract.companySigned && contract.personSigned);
  const isSignedByOne = draftStatus === 'signed_by_company' || draftStatus === 'signed_by_person';
  const draftReviewed = draftStatus === 'draft_approved' || draftStatus === 'draft_rejected';
  const draftSent = draftStatus !== 'none';
  const signingOutcome =
    contract.signingStatus === 'signed' || isSignedByBoth
      ? 'signed'
      : contract.signingStatus === 'declined' || draftStatus === 'draft_rejected'
        ? 'declined'
        : null;
  const signingOutcomeAt = signingOutcome === 'signed'
    ? contract.signingCompletedAt
    : contract.signingRejectedAt ?? contract.signingCompletedAt;
  const signingOutcomeAtLabel = signingOutcomeAt
    ? new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(new Date(signingOutcomeAt))
    : null;
  // The customer sent the draft back for changes (not a declined signing):
  // shown as its own event, with the requested changes (ClickUp 869fftzmh).
  const returnedDraft = draftStatus === 'draft_rejected' && contract.signingStatus !== 'declined' && !isSignedByBoth;
  const returnedAtLabel = returnedDraft && contract.draftRespondedAt
    ? new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(new Date(contract.draftRespondedAt))
    : null;
  // The company sent a changed agreement after the customer sent it back: the
  // customer's turn to review and sign it again (ClickUp 869fg9feb).
  const revisedDraft = !returnedDraft && !signingOutcome && isRevisedDraft(contract);
  const revisedAtLabel = revisedDraft && contract.draftSentAt
    ? new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(new Date(contract.draftSentAt))
    : null;
  const outcomeKey = returnedDraft ? 'returned' : revisedDraft ? 'revised' : signingOutcome;
  const outcomeEvent = revisedDraft
    ? t(`contracts.timeline.revised.${viewerRole === 'person' ? 'eventPerson' : 'eventCompany'}${revisedAtLabel ? 'WithDate' : ''}`, {
        number: contract.contractNumber,
        date: revisedAtLabel ?? ''
      })
    : returnedDraft
    ? t(`contracts.timeline.returned.${viewerRole === 'person' ? 'eventPerson' : 'eventCompany'}${returnedAtLabel ? 'WithDate' : ''}`, {
        number: contract.contractNumber,
        name: customerName?.trim() || t('contracts.timeline.returned.customer'),
        date: returnedAtLabel ?? ''
      })
    : signingOutcome ? t(
        signingOutcomeAtLabel
          ? `contracts.timeline.${signingOutcome}.eventWithDate`
          : `contracts.timeline.${signingOutcome}.event`,
        { date: signingOutcomeAtLabel ?? '' }
      ) : '';
  const outcomeMessage = returnedDraft
    ? contract.draftRejectionMessage ?? contract.signingRejectionMessage
    : signingOutcome === 'declined' ? contract.signingRejectionMessage : null;

  const contractAmount = parseFloat(String(contract.contractAmount || '0').replace(/[^0-9.-]/g, '')) || 0;
  const isVariationWithoutAmount =
    contract.contractType === 'variation' && contractAmount <= 0;
  const fundingSecured = pendingReplacement ? false : projectFunded ?? (
    contractAmount > 0 && (contract.paidAmount || 0) >= contractAmount
  );

  const sortedMilestones = combinedRoot ? milestones : [...milestones].sort((a, b) => a.milestoneNumber - b.milestoneNumber);
  const hasMilestones = sortedMilestones.length > 0;
  const emptyAmendment = agreementProjection && !!contract.stageVariation && !hasMilestones;
  const contractHasEnteredMilestoneWorkflow =
    hasMilestones &&
    (signingOutcome === 'signed' ||
      isSignedByBoth ||
      (contract.companySigned && contract.personSigned));
  const usesFlexibleMilestoneFlow = !!combinedRoot || sortedMilestones.some(milestone =>
    Boolean(milestone.workStartedAt) ||
    Boolean(milestone.workStartedByRole) ||
    Boolean(milestone.companyStartDecision) ||
    Boolean(milestone.startPredecessorIds?.length)
  );
  const firstAwaitingJointAttestation = sortedMilestones.findIndex(candidate =>
    !isReplacedMilestone(candidate) && (candidate.companyAttestation !== 'approved' || candidate.personAttestation !== 'approved')
  );

  const effectiveDeliveryStatus = (milestone: Milestone, index: number) => {
    if (isReplacedMilestone(milestone)) return effectiveTimelineDeliveryStatus(milestone);
    if (milestone.deliveryStatus === 'paid') return 'paid' as const;
    if (milestone.deliveryStatus === 'payout_failed' && fundingMilestones?.some(item =>
      item.milestoneId === milestone.id && item.payoutAvailability?.status === 'awaiting_funds')) return 'payout_processing' as const;
    if (
      !usesFlexibleMilestoneFlow &&
      firstAwaitingJointAttestation >= 0 &&
      index > firstAwaitingJointAttestation
    ) {
      return 'not_started' as const;
    }
    return effectiveTimelineDeliveryStatus(milestone);
  };
  const currentWorkflowMilestone = currentContractWorkflowMilestone(sortedMilestones);
  const currentWorkflowMilestoneIndex = currentWorkflowMilestone
    ? sortedMilestones.findIndex(milestone => milestone.id === currentWorkflowMilestone.id)
    : -1;
  const currentWorkflowStatus = currentWorkflowMilestone && currentWorkflowMilestoneIndex >= 0
    ? effectiveDeliveryStatus(currentWorkflowMilestone, currentWorkflowMilestoneIndex)
    : null;
  const legacyAttestationState = (m: Milestone): StepState => {
    const c = m.companyAttestation || 'not_attested';
    const p = m.personAttestation || 'not_attested';
    if (c === 'rejected' || p === 'rejected' || c === 'dispute' || p === 'dispute') return 'error';
    if (c === 'approved' && p === 'approved') return 'done';
    if (c === 'approved' || p === 'approved') return 'current';
    return 'pending';
  };

  // Build steps
  const utkastState: StepState = draftSent ? 'done' : (draftStatus === 'none' ? 'current' : 'pending');
  const granskningState: StepState =
    draftStatus === 'draft_rejected' ? 'error' :
    draftReviewed || isSignedByOne || isSignedByBoth ? 'done' :
    draftStatus === 'draft_sent' ? 'current' :
    'pending';
  const signeringState: StepState =
    isSignedByBoth ? 'done' :
    isSignedByOne ? 'current' :
    draftReviewed ? 'current' :
    'pending';
  const contractSteps: Step[] = [
    { type: 'single', key: 'utkast', label: t('contractTimeline.draft', 'Utkast'), state: utkastState },
    { type: 'single', key: 'granskning', label: t('contractTimeline.review', 'Granskning'), state: granskningState },
    { type: 'single', key: 'signerat', label: t('contractTimeline.signed', 'Signerat'), state: signeringState }
  ];
  const phases: PhaseStep[] = [
    {
      type: 'single',
      key: 'contract-phase',
      label: t('contractTimeline.contract', 'Kontrakt'),
      state: phaseState(contractSteps),
      children: contractSteps
    }
  ];

  // The buyer funds the complete project once. Each milestone therefore tracks
  // delivery, joint attestation and payout — never another customer payment.
  if (hasMilestones) {
    const firstUnfinishedIdx = sortedMilestones.findIndex(m => !isMilestoneSettledForClosure(m));

    sortedMilestones.forEach((m, idx) => {
      const isFirstUnfinished = idx === firstUnfinishedIdx;
      const isPriceAdjustment = m.itemType === 'variation_price_adjustment';
      const isZeroValuePriceAdjustment = isPriceAdjustment && m.amount === 0;
      const deliveryStatus = effectiveDeliveryStatus(m, idx);
      const legacyState = legacyAttestationState(m);
      const workState: StepState = (() => {
        if (!deliveryStatus) {
          if (legacyState !== 'pending') return 'done';
          return isFirstUnfinished && fundingSecured ? 'current' : 'pending';
        }
        if (deliveryStatus === 'not_started') {
          if (combinedRoot) return 'pending';
          return isFirstUnfinished && fundingSecured ? 'current' : 'pending';
        }
        if (deliveryStatus === 'in_progress' || deliveryStatus === 'under_resolution') {
          return 'current';
        }
        return 'done';
      })();
      const attestationState: StepState = (() => {
        if (!deliveryStatus) return legacyState;
        if (deliveryStatus === 'conflict' || deliveryStatus === 'external_dispute') return 'error';
        if (deliveryStatus === 'disputed') return 'warning';
        if (legacyState === 'current') return 'current';
        if (
          deliveryStatus === 'ready_for_attestation' ||
          deliveryStatus === 'seller_attested' ||
          deliveryStatus === 'ready_for_reattestation'
        ) {
          return 'current';
        }
        if (
          deliveryStatus === 'jointly_attested' ||
          deliveryStatus === 'payout_processing' ||
          deliveryStatus === 'payout_failed' ||
          deliveryStatus === 'paid'
        ) {
          return 'done';
        }
        return 'pending';
      })();
      const payoutState: StepState = (() => {
        if (deliveryStatus === 'paid') return 'done';
        if (deliveryStatus === 'payout_failed') return 'error';
        if (
          deliveryStatus === 'jointly_attested' ||
          deliveryStatus === 'payout_processing' ||
          (!deliveryStatus && legacyState === 'done')
        ) {
          return 'current';
        }
        return 'pending';
      })();

      const milestoneSteps: Step[] = [
        {
          type: 'single',
          key: `work-${m.id}`,
          label: deliveryStatus === 'under_resolution'
            ? t('contractTimeline.beingRemedied', 'Åtgärdas')
            : isPriceAdjustment
            ? t('contractTimeline.basis', 'Underlag')
            : t('contractTimeline.work', 'Arbete'),
          state: workState
        },
        {
          type: 'single',
          key: `attest-${m.id}`,
          label: deliveryStatus === 'external_dispute'
            ? t('contractTimeline.dispute', 'Tvist')
            : deliveryStatus === 'disputed'
            ? t('contractTimeline.disputed', 'Under invändning')
            : deliveryStatus === 'conflict'
              ? t('contractTimeline.conflict', 'Konflikt')
              // While only one side has attested, the step names who it waits for;
              // the side it waits for reads "your turn" instead of its own role.
              : attestationState === 'current' && m.companyAttestation === 'approved' && m.personAttestation !== 'approved'
                ? t(viewerRole === 'person' ? 'contractTimeline.attestationYourTurn' : 'contractTimeline.attestationAwaitingPerson')
                : attestationState === 'current' && m.personAttestation === 'approved' && m.companyAttestation !== 'approved'
                  ? t(viewerRole === 'company' ? 'contractTimeline.attestationYourTurn' : 'contractTimeline.attestationAwaitingCompany')
                  : t('contractTimeline.attestation', 'Attestering'),
          state: attestationState
        },
        {
          type: 'single',
          key: `payout-${m.id}`,
          // The steps are shown for one stage at a time, so they carry no stage number.
          label: t('contractTimeline.payout', 'Utbetalning'),
          state: payoutState
        }
      ];

      phases.push({
        type: 'single',
        key: `phase-${m.id}`,
        label: isPriceAdjustment
          ? isZeroValuePriceAdjustment
            ? t('contractTimeline.variation', 'ÄTA')
            : t('contractTimeline.variationPriceChange', 'ÄTA-prisändring')
          : sortedMilestones.length > 1
            ? t('contractTimeline.stageNumbered', 'Etapp {{number}}', { number: m.milestoneNumber })
            : t('contractTimeline.stage', 'Etapp'),
        state: phaseState(milestoneSteps),
        children: milestoneSteps
      });
    });
  } else {
    phases.push({
      type: 'single',
      key: 'milestone-plan-missing',
      label: emptyAmendment ? t('stageVariation.noRemainingWork') : isVariationWithoutAmount
        ? t('contractTimeline.variation', 'ÄTA')
        : t('contractTimeline.milestonePlanMissing', 'Etappplan saknas'),
      state: emptyAmendment ? agreementReadOnly ? 'pending' : 'done' : isSignedByBoth && !isVariationWithoutAmount ? 'error' : 'pending'
    });
  }

  // A project can leave Etappio before every contract phase has been completed,
  // for example after an inactivity settlement or when a dispute is handed off
  // externally. Keep genuinely completed phases green and the disputed phase
  // red, but render all interrupted or unreached work as closed.
  if (projectCompleted || projectExternallyDisputed) {
    const externalDisputePhaseKey = projectExternallyDisputed
      ? sortedMilestones.find(milestone => milestone.deliveryStatus === 'external_dispute')?.id
      : undefined;
    phases.forEach(phase => {
      if (phase.state === 'done') return;
      if (externalDisputePhaseKey && phase.key === `phase-${externalDisputePhaseKey}`) return;
      phase.state = 'closed';
      phase.children = phase.children?.map(child => (
        child.state === 'done' ? child : { ...child, state: 'closed' }
      ));
    });
  }

  // Final "Avslut" step — normally reached after every milestone payout, but
  // also reached when a terminal settlement closes the project early.
  const contractFullyClosed = projectCompleted || (emptyAmendment && !agreementReadOnly) || (
    hasMilestones && isSignedByBoth && (fundingSecured || contract.versionStatus === 'superseded') && sortedMilestones.every(isMilestoneSettledForClosure)
  );
  const avslutState: StepState =
    projectExternallyDisputed ? 'closed' :
    contractFullyClosed ? 'done' :
    'pending';
  phases.push({
    type: 'single',
    key: 'avslut',
    label: projectExternallyDisputed
      ? t('contractTimeline.closedExternalDispute', 'Avslutat – extern tvist')
      : projectCompleted && !isSignedByBoth
        ? t('contractTimeline.closedNotExecuted', 'Avslutat – ej tecknat')
        : t('contractTimeline.closing', 'Avslut'),
    state: avslutState
  });

  const currentWorkflowEvent = (() => {
    if (
      !contractHasEnteredMilestoneWorkflow ||
      !currentWorkflowMilestone ||
      !currentWorkflowStatus
    ) return null;
    const subject = currentWorkflowMilestone.itemType === 'variation_price_adjustment'
      ? currentWorkflowMilestone.amount === 0
        ? t('contractTimeline.variation', 'ÄTA')
        : t('contractTimeline.variationPriceChange', 'ÄTA-prisändring')
      : t('contractTimeline.stageNumbered', 'Etapp {{number}}', {
          number: currentWorkflowMilestone.milestoneNumber
        });

    return timelineDeliveryPresentation(currentWorkflowStatus, t, subject);
  })();
  // In a contract frame the status line (badge + event) sits in the frame's
  // header; a signing outcome's message stays above the timeline.
  const framed = !!renderFrame;
  const statusAlertClass = currentWorkflowEvent
    ? currentWorkflowEvent.alertClass
    : signingOutcome === 'signed' && !returnedDraft ? 'alert-success' : revisedDraft ? 'alert-info' : 'alert-warning';
  const statusAttrs = currentWorkflowEvent
    ? { 'data-testid': 'contract-current-timeline-event', 'data-delivery-status': currentWorkflowEvent.status }
    : { 'data-testid': 'contract-signing-timeline-event', 'data-signing-status': outcomeKey ?? undefined };
  const statusLine = currentWorkflowEvent ? <>
    <span className={`badge ${currentWorkflowEvent.badgeClass}`}>
      <i className={`feather-${currentWorkflowEvent.icon} me-1`}></i>
      {currentWorkflowEvent.label}
    </span>
    <span className="small fw-semibold">{currentWorkflowEvent.event}</span>
  </> : outcomeKey ? <>
    <span className={`badge ${outcomeKey === 'signed' ? 'bg-success' : outcomeKey === 'revised' ? 'bg-info text-dark' : 'bg-warning text-dark'}`}>
      <i className={`feather-${outcomeKey === 'signed' ? 'check-circle' : outcomeKey === 'returned' ? 'corner-up-left' : outcomeKey === 'revised' ? 'edit-3' : 'x-circle'} me-1`}></i>
      {t(`contracts.timeline.${outcomeKey}.status`)}
    </span>
    <span className="small fw-semibold">{outcomeEvent}</span>
  </> : null;
  // An objection's reason and dialogue live in its modal and the stage node
  // says "Under invändning"; the frame does not repeat them (ClickUp 869fj6n8x).
  const statusDetails = currentWorkflowEvent ? null : outcomeMessage ? (
    <div className={framed ? 'small' : 'small mt-1'} data-testid="contract-outcome-message">
      {returnedDraft && <strong>{t('contracts.timeline.returned.requestedChanges')}</strong>}{returnedDraft && ' '}
      {outcomeMessage}
    </div>
  ) : null;
  const hasStatusDetails = !!statusDetails;

  const workflowSummary = !statusLine ? null : framed
    ? hasStatusDetails ? <div className={`alert py-2 px-3 mb-3 ${statusAlertClass}`}>{statusDetails}</div> : null
    : (
      <div className={`alert py-2 px-3 mb-3 ${statusAlertClass}`} {...statusAttrs} role="status">
        <div className="d-flex flex-wrap align-items-center gap-2">{statusLine}</div>
        {statusDetails}
      </div>
    );
  // Header variant: a soft pill toned like the status alert, plus plain text.
  const statusTone = statusAlertClass.replace('alert-', '');
  const headerStatusParts: FrameStatusParts | undefined = framed && statusLine ? {
    attrs: statusAttrs,
    pill: <span className={`contract-group-status-pill is-${statusTone}`}>
      {currentWorkflowEvent ? currentWorkflowEvent.label : t(`contracts.timeline.${outcomeKey}.status`)}
    </span>,
    event: <span className="contract-group-status-event">
      {currentWorkflowEvent ? currentWorkflowEvent.event : outcomeEvent}
    </span>
  } : undefined;
  const headerStatus = headerStatusParts ? (
    <div className="contract-group-status" {...headerStatusParts.attrs} role="status">
      {headerStatusParts.pill}
      {headerStatusParts.event}
    </div>
  ) : null;

  // All roles and contract types use this renderer, including drafts and price-only ÄTA.
  const timeline = (
      <MilestoneFlowTimeline
        contract={contract}
        combinedRoot={combinedRoot}
        preserveMilestoneOrder={!!combinedRoot}
        milestoneContexts={milestoneContexts}
        contractSteps={phases[0].children ?? contractSteps}
        contractState={phases[0].state}
        closingLabel={phases[phases.length - 1].label}
        emptyPhase={hasMilestones ? undefined : phases.find(phase => phase.key === 'milestone-plan-missing')}
        emptyPlanComplete={emptyAmendment && !agreementReadOnly}
        milestones={sortedMilestones}
        milestonePhases={Object.fromEntries(sortedMilestones.map((milestone, index) => {
          const phase = phases.find(phase => phase.key === `phase-${milestone.id}`);
          return [milestone.id, {
            state: phase?.state ?? 'pending', label: phase?.label,
            deliveryStatus: effectiveDeliveryStatus(milestone, index),
            steps: phase?.children ?? []
          }];
        }))}
        parentContract={contract.stageVariation && relatedContracts ? agreementRoot(contract, relatedContracts) : parentContract}
        projectCompleted={projectCompleted}
        projectExternallyDisputed={projectExternallyDisputed}
        onManageStarts={onManageStarts ? milestone => {
          const source = projection ? projection.actionSources.get(milestone.id) : milestone;
          if (source) onManageStarts(source);
        } : undefined}
        isMilestoneReadOnly={milestoneContexts ? milestone => !!milestoneContexts[milestone.id]?.pending : agreementReadOnly ? () => true : projection ? milestone => !projection.actionSources.has(milestone.id) : undefined}
        canManageStarts={canManageStarts && !agreementReadOnly}
        inferSequentialOrder={!!combinedRoot || !usesFlexibleMilestoneFlow}
        workflowStatusSummary={<>
          {pendingReplacement && (
            <div className="alert alert-warning py-2 mb-3" data-testid="funding-replacement-context" role="status">
              <strong>{t(contract.stageVariation ? timelineFundingNeedOre(contract) > 0 ? 'stageVariation.state.funding' : 'stageVariation.state.activating' : timelineFundingNeedOre(contract) > 0 ? 'contractTimeline.fundingReplacementTitle' : 'contractTimeline.activatingReplacementTitle', { number: agreementVariationNumber(contract) ?? contract.contractNumber, next: contract.contractNumber, previous: currentContract.contractNumber })}</strong>
              <div className="small mt-1">{t(contract.stageVariation ? 'stageVariation.noticeHelp' : 'contractTimeline.fundingReplacementHint', { previous: currentContract.contractNumber })}</div>
            </div>
          )}
          {workflowSummary}
        </>}
        viewerRole={viewerRole}
        fundingMilestones={fundingMilestones}
        fundingContract={fundingContract}
        onPayContract={onPayContract}
        onSignContract={onSignContract}
        actionMilestoneIds={actionNodeIds}
        onReviseContract={onReviseContract}
        framed={!!renderFrame}
        contractSigningDisabled={contractSigningDisabled}
        renderMilestoneActions={milestoneContexts ? (milestone, closeDetails) => milestoneContexts[milestone.id]?.pending ? null : renderMilestoneActions?.(milestone, closeDetails) : agreementReadOnly ? undefined : projection ? (milestone, closeDetails) => {
          const source = projection.actionSources.get(milestone.id);
          if (!source) return <p className="small text-muted mb-0">{t('contractTimeline.replacementStageReadOnly')}</p>;
          return <>
            <p className="small text-muted">{t('contractTimeline.replacementStageActions', { number: currentContract.contractNumber })}</p>
            {renderMilestoneActions?.(source, closeDetails)}
          </>;
        } : renderMilestoneActions}
      />
  );
  return renderFrame ? renderFrame({ status: headerStatus, statusParts: headerStatusParts, timeline, closed: avslutState === 'done' }) : timeline;
}
